import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  Inject,
  OnInit,
  ViewChild,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MaterialModule } from '../../material.module';
import { SvgIconComponent } from '../svg-icon/svg-icon.component';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MatTableDataSource } from '@angular/material/table';
import { MatSort } from '@angular/material/sort';
import {
  gridMessages,
  lookupHeaders,
  lookupPluralHeaders,
} from '../../utility/constants';
import {
  CdkVirtualScrollViewport,
  ScrollingModule,
} from '@angular/cdk/scrolling';
import { ITableProp } from '../../models/table.model';

@Component({
  selector: 'app-lookup-popup',
  standalone: true,
  imports: [CommonModule, SvgIconComponent, MaterialModule, ScrollingModule],
  templateUrl: './lookup-popup.component.html',
  styleUrl: './lookup-popup.component.scss',
})
export class LookupPopupComponent implements OnInit, AfterViewInit {
  @ViewChild(MatSort) sort!: MatSort;
  @ViewChild(CdkVirtualScrollViewport) viewport!: CdkVirtualScrollViewport;

  public crudPopupData: any;
  public searchTextbox!: string;
  public lookupTableDataSource = new MatTableDataSource<any>([]);
  public lookupTableDisplayedColumns!: string[];
  public lookupTableColumns!: ITableProp[];
  public tableSortDirection!: string;
  public noResultsMessage = '';

  private readonly pageSize = 100;
  private allData: any[] = [];
  private currentIndex = this.pageSize;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    private readonly ref: MatDialogRef<LookupPopupComponent>,
    private readonly cdref: ChangeDetectorRef,
    private readonly destroyRef: DestroyRef,
  ) {
    this.tableSortDirection = 'desc';
  }

  ngOnInit(): void {
    this.crudPopupData = this.data;
    this.lookupTableDisplayedColumns =
      this.crudPopupData.code.displayedColumnsList;
    this.lookupTableColumns = this.crudPopupData.code.tableColumns;
    this.allData = this.crudPopupData.code.tableData;
    this.getnoMatchMessage();
    this.lookupTableDataSource.data = this.allData.slice(0, this.pageSize);
  }

  ngAfterViewInit() {
    this.getDataWithSort();
  }

  public closePopup() {
    this.ref.close(true);
  }

  public getPlaceholderText() {
    return `Search ${this.crudPopupData.title}`;
  }

  public filterLookUpList() {
    const modifiedSearchValue =
      this.searchTextbox !== ''
        ? this.searchTextbox.replace(/[\s-]/g, '')?.toLowerCase()
        : '';
    let filteredData = this.allData;
    if (modifiedSearchValue) {
      filteredData = this.allData.filter((opt: any) =>
        this.lookupTableDisplayedColumns.some((col) =>
          opt[col] != null && this.valueMatchesSearch(opt[col], modifiedSearchValue)
        )
      );
    }
    this.lookupTableDataSource.data = filteredData.slice(0, this.pageSize);
    this.currentIndex = this.pageSize;
  }

  public onSelectClicked(rowData: any) {
    this.ref.close({ popupClose: true, data: rowData });
  }

  public getMatchedRow(rowData: any) {
    return (
      rowData[this.lookupTableDisplayedColumns[0]] ===
      this.crudPopupData.code.selectedOptionData
    );
  }

  private getDataWithSort() {
    this.sort.active = this.lookupTableDisplayedColumns[0];
    this.lookupTableDataSource.sort = this.sort;
    this.cdref.detectChanges();
    this.sort.sortChange.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(data => {
      this.tableSortDirection = data.direction;
    });
  }

  private getnoMatchMessage() {
    if (this.crudPopupData.title === lookupHeaders.country) {
      this.noResultsMessage = gridMessages.noRecordsMatch(
        lookupPluralHeaders.countries
      );
    } else if (this.crudPopupData.title === lookupHeaders.state) {
      this.noResultsMessage = gridMessages.noRecordsMatch(
        lookupPluralHeaders.states
      );
    } else if (this.crudPopupData.title === lookupHeaders.city) {
      this.noResultsMessage = gridMessages.noRecordsMatch(
        lookupPluralHeaders.cities
      );
    } else {
      this.noResultsMessage = '';
    }
  }

  public onScroll() {
    if (!this.viewport.measureScrollOffset('bottom')) {
      const filteredData = this.filterDataBasedOnSearch();
      if (this.currentIndex < filteredData.length) {
        this.currentIndex = Math.min(
          this.currentIndex + this.pageSize,
          filteredData.length
        );
        this.lookupTableDataSource.data = filteredData.slice(0, this.currentIndex);
      }
    }
  }

  private filterDataBasedOnSearch() {
    const modifiedSearchValue =
      this.searchTextbox !== ''
        ? this.searchTextbox?.replace(/[\s-]/g, '')?.toLowerCase()
        : '';
    return modifiedSearchValue
      ? this.allData.filter((opt: any) =>
          this.lookupTableDisplayedColumns.some((col) =>
            opt[col] != null && this.valueMatchesSearch(opt[col], modifiedSearchValue)
          )
        )
      : this.allData;
  }

  /**
   * Splits a column value on the display separator " - " and checks whether
   * ANY token starts with the search string (after stripping spaces/hyphens).
   * e.g. "UY - URUGUAY" → ["UY", "URUGUAY"] → "uruguay".startsWith("ur") ✓
   */
  private valueMatchesSearch(value: string, search: string): boolean {
    return value
      .split(' - ')
      .some(part => part.replace(/[\s-]/g, '').toLowerCase().startsWith(search));
  }
}
